import type { BillingCheckoutSelect, BillingPortalSelect } from '@urbanmetrix/sdk';
import type { BillingPlan } from './planApi';
import { apiClient, throwApiError } from './apiClient';
export type BillingPortal = BillingPortalSelect;
export type BillingCheckout = BillingCheckoutSelect;
export type CustomPlanCheckoutResponse = { url?: string; message?: string };
export type CustomPlanUnlockResponse = {
  plan: BillingPlan;
  unlockToken: string;
};
export type CustomBundleUnlockResponse = {
  bundle: BillingBundle;
  unlockToken: string;
};

type ApiResponse<T> = Promise<{ data: T; error: { value: unknown; status: number } | null }>;
type CheckoutPayload = {
  priceLookupKey: string;
  trialPriceLookupKey?: string;
  unlockToken?: string | null;
  successUrl: string;
  cancelUrl: string;
  disableRedirect?: boolean;
};

const billingApi = apiClient.api.billing as unknown as {
  bundles: { get: () => ApiResponse<BillingBundle[]> };
  checkout: { post: (payload: CheckoutPayload) => ApiResponse<BillingCheckout> };
  'unlock-custom-bundle': {
    post: (payload: { priceLookupKey: string }) => ApiResponse<CustomBundleUnlockResponse>;
  };
  'unlock-custom-plan': { post: (payload: { priceLookupKey: string }) => ApiResponse<CustomPlanUnlockResponse> };
  'upgrade-subscription': { post: (payload: CheckoutPayload) => ApiResponse<BillingCheckout> };
  'downgrade-subscription': {
    post: (payload: { priceLookupKey: string; unlockToken?: string | null }) => ApiResponse<void>;
  };
};

export type BillingBundle = {
  category: 'bundle';
  name: string;
  productId: string;
  productName: string;
  productDescription: string | null;
  quantity: number;
  price: {
    priceId: string;
    lookupKey: string;
    currency: string;
    unitAmount: number | null;
    unitAmountDecimal: string | null;
  };
};

export async function getBundles(): Promise<BillingBundle[]> {
  const response = await billingApi.bundles.get();
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function unlockCustomBundle(priceLookupKey: string): Promise<CustomBundleUnlockResponse> {
  const response = await billingApi['unlock-custom-bundle'].post({ priceLookupKey });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function cancelSubscriptionRenewal(returnUrl: string): Promise<BillingPortal> {
  const response = await apiClient.api.billing['cancel-renewal'].post({
    returnUrl,
  });
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function restoreSubscriptionRenewal(): Promise<void> {
  const response = await apiClient.api.billing['restore-renewal'].post();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  return response.data;
}

export async function createCheckout(
  priceLookupKey: string,
  successUrl: string,
  cancelUrl: string,
  disableRedirect = true,
  trialPriceLookupKey?: string
): Promise<BillingCheckout> {
  const response = await billingApi.checkout.post({
    priceLookupKey,
    ...(trialPriceLookupKey ? { trialPriceLookupKey } : {}),
    unlockToken: null,
    successUrl,
    cancelUrl,
    disableRedirect,
  });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function createCustomPlanCheckout(
  priceLookupKey: string,
  unlockToken: string,
  successUrl: string,
  cancelUrl: string,
  disableRedirect = true,
  trialPriceLookupKey?: string
): Promise<CustomPlanCheckoutResponse> {
  const response = await billingApi.checkout.post({
    priceLookupKey,
    ...(trialPriceLookupKey ? { trialPriceLookupKey } : {}),
    unlockToken,
    successUrl,
    cancelUrl,
    disableRedirect,
  });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function unlockCustomPlan(priceLookupKey: string): Promise<CustomPlanUnlockResponse> {
  const response = await billingApi['unlock-custom-plan'].post({ priceLookupKey });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function upgradeSubscription(
  priceLookupKey: string,
  successUrl: string,
  cancelUrl: string,
  disableRedirect = true
): Promise<BillingCheckout> {
  const response = await billingApi['upgrade-subscription'].post({
    priceLookupKey,
    unlockToken: null,
    successUrl,
    cancelUrl,
    disableRedirect,
  });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function downgradeSubscription(priceLookupKey: string): Promise<void> {
  const response = await billingApi['downgrade-subscription'].post({ priceLookupKey, unlockToken: null });
  if (response.error) throwApiError(response.error.value, response.error.status);
}

export async function upgradeCustomSubscription(
  priceLookupKey: string,
  unlockToken: string,
  successUrl: string,
  cancelUrl: string,
  disableRedirect = true
): Promise<BillingCheckout> {
  const response = await billingApi['upgrade-subscription'].post({
    priceLookupKey,
    unlockToken,
    successUrl,
    cancelUrl,
    disableRedirect,
  });
  if (response.error) throwApiError(response.error.value, response.error.status);
  return response.data;
}

export async function downgradeCustomSubscription(priceLookupKey: string, unlockToken: string): Promise<void> {
  const response = await billingApi['downgrade-subscription'].post({ priceLookupKey, unlockToken });
  if (response.error) throwApiError(response.error.value, response.error.status);
}
